// Presets visuais por empresa (slug), derivados da identidade de marca
const COMPANY_PRESETS = {
  // Fina Arte: elegante, feminino, artesanal — logo rosa-nuvem com script delicado
  "fina-arte": {
    accent_color: "#D4778A",
    accent_dark: "#A8506A",
    gradient_from: "#2A0E1E",
    gradient_to: "#3D1830",
    bg: "#FEF5F7",
    surface: "#ffffff",
    surface_warm: "#FFF0F3",
    border: "#F0D0DC",
    border_light: "#F8E8EE",
    text: "#2A0E1E",
    text_muted: "#7A4A5A",
    text_light: "#B08090",
    font: "Inter",
    font_heading: "Playfair Display",
    font_brand: "Dancing Script",
    radius_sm: "8px",
    radius_md: "12px",
    radius_lg: "16px",
    radius_xl: "20px",
    radius_2xl: "24px",
  },

  // Kindu: dark — fundo quase-preto, vermelho vibrante, sidebar verde-noite, ouro no header
  kindu: {
    accent_color: "#E52222",
    accent_dark: "#FF6060", // tom mais claro para legibilidade como texto sobre fundo escuro
    gradient_from: "#060E07",
    gradient_to: "#112015",
    bg: "#0C0C0C",
    surface: "#1C1C1C", // cards/modais/inputs — elevação sobre o fundo
    surface_warm: "#191919", // cabeçalhos de card
    border: "#2E2E2E",
    border_light: "#242424",
    text: "#EDEBE7",
    text_muted: "#9A9A92", // suficiente contraste AA sobre #1C1C1C
    text_light: "#5C5C56",
    font: "Nunito",
    font_heading: "Nunito",
    font_brand: "Fredoka One",
    radius_sm: "10px",
    radius_md: "14px",
    radius_lg: "18px",
    radius_xl: "22px",
    radius_2xl: "28px",
  },

  // MaBe: alegre, colorido, lúdico — logo multicolorido com elementos de papel
  mabe: {
    accent_color: "#FF3D7F",
    accent_dark: "#CC1A5A",
    gradient_from: "#1A0A38",
    gradient_to: "#2D1260",
    bg: "#FAFAFF",
    surface: "#ffffff",
    surface_warm: "#F5F0FF",
    border: "#E0D8F8",
    border_light: "#EDE8FC",
    text: "#1A0A30",
    text_muted: "#5A4A7A",
    text_light: "#9080B0",
    font: "Nunito",
    font_heading: "Nunito",
    font_brand: "Fredoka One",
    radius_sm: "12px",
    radius_md: "16px",
    radius_lg: "20px",
    radius_xl: "24px",
    radius_2xl: "32px",
  },
};

const DEFAULT_THEME = {
  accent_color: "#c9956c",
  accent_dark: "#a87550",
  gradient_from: "#162824",
  gradient_to: "#2a4840",
  bg: "#f2ebe2",
  surface: "#ffffff",
  surface_warm: "#faf5ef",
  border: "#e4d4c8",
  border_light: "#ede4da",
  text: "#261e1a",
  text_muted: "#7a6a60",
  text_light: "#a8988e",
  font: "Inter",
  font_heading: "Playfair Display",
  font_brand: "Dancing Script",
  radius_sm: "8px",
  radius_md: "12px",
  radius_lg: "16px",
  radius_xl: "20px",
  radius_2xl: "24px",
};

// As 4 cores da marca: campos da empresa no banco têm precedência sobre o
// preset do slug, que tem precedência sobre o tema padrão. Usado pelo tema
// e pelos PDFs, para os dois saírem com a mesma identidade.
export function resolveBrandColors(company) {
  const preset = company?.slug ? COMPANY_PRESETS[company.slug] : null;
  const base = preset || DEFAULT_THEME;
  return {
    accent: company?.accent_color || base.accent_color,
    accentDark: company?.accent_dark || base.accent_dark,
    gradientFrom: company?.gradient_from || base.gradient_from,
    gradientTo: company?.gradient_to || base.gradient_to,
  };
}

export function applyTheme(company) {
  const preset = company?.slug ? COMPANY_PRESETS[company.slug] : null;
  const base = preset || DEFAULT_THEME;
  const { accent, accentDark, gradientFrom, gradientTo } =
    resolveBrandColors(company);

  const root = document.documentElement;

  // Accent scale
  root.style.setProperty("--c-accent", accent);
  root.style.setProperty("--c-accent-dark", accentDark);
  root.style.setProperty("--c-accent-mid", lightenHex(accent, 18));
  root.style.setProperty("--c-accent-bg", hexToRgba(accent, 0.08));
  root.style.setProperty("--c-accent-light", hexToRgba(accent, 0.18));
  root.style.setProperty("--c-accent-xlt", hexToRgba(accent, 0.07));
  root.style.setProperty("--c-accent-glow", hexToRgba(accent, 0.28));

  // Backwards-compat aliases usados nas views
  root.style.setProperty("--c-primary", accent);
  root.style.setProperty("--c-primary-dark", accentDark);
  root.style.setProperty("--c-primary-light", hexToRgba(accent, 0.18));
  root.style.setProperty("--c-primary-xlt", hexToRgba(accent, 0.07));

  // Gradient & Sidebar
  root.style.setProperty("--c-gradient-from", gradientFrom);
  root.style.setProperty("--c-gradient-to", gradientTo);
  root.style.setProperty("--c-sidebar", gradientFrom);
  root.style.setProperty("--c-sidebar-accent", accent);
  root.style.setProperty("--c-sidebar-active", hexToRgba(accent, 0.15));
  root.style.setProperty("--c-sidebar-border", hexToRgba(accent, 0.25));
  root.style.setProperty("--c-sidebar-hover", hexToRgba("#ffffff", 0.07));
  root.style.setProperty("--c-sidebar-text", tintedWhite(accent, 0.88));
  root.style.setProperty("--c-sidebar-muted", tintedWhite(accent, 0.46));
  root.style.setProperty("--c-sidebar-section", tintedWhite(accent, 0.32));

  // Surface & Background
  root.style.setProperty("--c-bg", base.bg);
  root.style.setProperty("--c-surface", base.surface || "#ffffff");
  root.style.setProperty("--c-surface-warm", base.surface_warm);
  root.style.setProperty("--c-border", base.border);
  root.style.setProperty("--c-border-light", base.border_light);

  // Sombras adaptadas ao tema: escuro usa rgba(0,0,0) em vez do marrom quente padrão
  const surfaceR = parseInt((base.surface || "#ffffff").slice(1, 3), 16);
  // Telas com cores próprias (ex.: listas por semana) trocam a paleta
  root.dataset.tema = surfaceR < 80 ? "escuro" : "claro";
  if (surfaceR < 80) {
    root.style.setProperty(
      "--sh-xs",
      "0 1px 2px rgba(0,0,0,0.4), 0 1px 4px rgba(0,0,0,0.28)"
    );
    root.style.setProperty(
      "--sh-sm",
      "0 1px 3px rgba(0,0,0,0.45), 0 4px 12px rgba(0,0,0,0.32)"
    );
    root.style.setProperty(
      "--sh-md",
      "0 2px 6px rgba(0,0,0,0.5), 0 8px 24px rgba(0,0,0,0.38)"
    );
    root.style.setProperty(
      "--sh-lg",
      "0 4px 12px rgba(0,0,0,0.55), 0 16px 40px rgba(0,0,0,0.44)"
    );
    root.style.setProperty(
      "--sh-xl",
      "0 8px 24px rgba(0,0,0,0.6), 0 32px 64px rgba(0,0,0,0.5)"
    );
  } else {
    root.style.setProperty(
      "--sh-xs",
      "0 1px 2px rgba(38,30,26,0.06), 0 1px 4px rgba(38,30,26,0.04)"
    );
    root.style.setProperty(
      "--sh-sm",
      "0 1px 3px rgba(38,30,26,0.07), 0 4px 12px rgba(38,30,26,0.07)"
    );
    root.style.setProperty(
      "--sh-md",
      "0 2px 6px rgba(38,30,26,0.08), 0 8px 24px rgba(38,30,26,0.09)"
    );
    root.style.setProperty(
      "--sh-lg",
      "0 4px 12px rgba(38,30,26,0.1), 0 16px 40px rgba(38,30,26,0.12)"
    );
    root.style.setProperty(
      "--sh-xl",
      "0 8px 24px rgba(38,30,26,0.14), 0 32px 64px rgba(38,30,26,0.18)"
    );
  }

  // Texto
  root.style.setProperty("--c-text", base.text);
  root.style.setProperty("--c-text-muted", base.text_muted);
  root.style.setProperty("--c-text-light", base.text_light);

  // Tabela

  // Tipografia
  root.style.setProperty(
    "--font",
    `"${base.font}", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
  );
  root.style.setProperty(
    "--font-heading",
    base.font_heading === base.font
      ? `"${base.font_heading}", sans-serif`
      : `"${base.font_heading}", Georgia, serif`
  );
  root.style.setProperty("--font-brand", `"${base.font_brand}", cursive`);

  // Personalidade dos border-radius
  root.style.setProperty("--r-sm", base.radius_sm);
  root.style.setProperty("--r-md", base.radius_md);
  root.style.setProperty("--r-lg", base.radius_lg);
  root.style.setProperty("--r-xl", base.radius_xl);
  root.style.setProperty("--r-2xl", base.radius_2xl);
}

export function resetTheme() {
  applyTheme(null);
}

// Clareia um hex misturando com branco em `amount` % (0-100)
function lightenHex(hex, amount) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const mix = (c) => Math.min(255, Math.round(c + (255 - c) * (amount / 100)));
  return `#${mix(r).toString(16).padStart(2, "0")}${mix(g)
    .toString(16)
    .padStart(2, "0")}${mix(b).toString(16).padStart(2, "0")}`;
}

// Branco suavemente tingido pelo hue do accent
function tintedWhite(accentHex, opacity) {
  const r = parseInt(accentHex.slice(1, 3), 16);
  const g = parseInt(accentHex.slice(3, 5), 16);
  const b = parseInt(accentHex.slice(5, 7), 16);
  const tr = Math.round(255 * 0.88 + r * 0.12);
  const tg = Math.round(255 * 0.88 + g * 0.12);
  const tb = Math.round(255 * 0.88 + b * 0.12);
  return `rgba(${tr}, ${tg}, ${tb}, ${opacity})`;
}

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
